import type { useTranslations } from "next-intl"

type Translator = ReturnType<typeof useTranslations>

type LocalizableOption = {
  value: string
  label?: string
  name?: string
}

export const CREATE_OPTION_LABEL_KEYS = {
  ethnicity: {
    Caucasian: "caucasian",
    Asian: "asian",
    Latina: "latina",
    African: "african",
    Arab: "arab",
  },
  hairStyle: {
    Straight: "straight",
    "pixie cut": "short",
    "waist-length": "long",
    "high ponytail": "ponytail",
    Bun: "bun",
    "voluminous curly": "curly",
    "Box Braids": "braids",
  },
  hairColor: {
    Black: "black",
    Blonde: "blonde",
    Brunette: "brunette",
    Redhead: "redhead",
    White: "white",
    Silver: "silver",
    Pink: "pink",
    Blue: "blue",
    Green: "green",
    Purple: "purple",
  },
  eyeColor: {
    Black: "black",
    Blue: "blue",
    Brown: "brown",
    Green: "green",
    Hazel: "hazel",
    Grey: "grey",
    Amber: "amber",
    Red: "red",
    Violet: "violet",
  },
  bodyType: {
    slender: "slim",
    muscular: "athletic",
    BBW: "voluptuous",
    Curvy: "curvy",
  },
  breastSize: {
    petite: "small",
    Medium: "medium",
    Large: "large",
    Athletic: "athletic",
  },
  buttSize: {
    Small: "small",
    Medium: "medium",
    Large: "large",
    Athletic: "athletic",
  },
  personality: {
    flirty: "flirty",
    caring: "caring",
    adventurous: "adventurous",
    intellectual: "intellectual",
    mysterious: "mysterious",
    playful: "playful",
    dominant: "dominant",
    submissive: "submissive",
    confident: "confident",
    shy: "shy",
    romantic: "romantic",
    witty: "witty",
    sarcastic: "sarcastic",
    nurturing: "nurturing",
    bold: "bold",
    gentle: "gentle",
    ambitious: "ambitious",
    dreamy: "dreamy",
    fierce: "fierce",
    loyal: "loyal",
  },
  occupation: {
    none: "none",
    model: "model",
    student: "student",
    nurse: "nurse",
    teacher: "teacher",
    artist: "artist",
    dancer: "dancer",
    athlete: "athlete",
    musician: "musician",
    chef: "chef",
    scientist: "scientist",
    porn_star: "pornStar",
  },
  relationship: {
    girlfriend: "girlfriend",
    friend: "friend",
    wife: "wife",
    step_sister: "stepSister",
    colleague: "colleague",
    neighbor: "neighbor",
    stranger: "stranger",
    mentor: "mentor",
  },
  hobby: {
    none: "none",
    yoga: "yoga",
    gaming: "gaming",
    cooking: "cooking",
    dancing: "dancing",
    reading: "reading",
    photography: "photography",
    traveling: "traveling",
    fitness: "fitness",
    music: "music",
    art: "art",
  },
  fetish: {
    none: "none",
    bdsm: "bdsm",
    roleplay: "roleplay",
    feet: "feet",
    leather: "leather",
    cosplay: "cosplay",
    voyeur: "voyeur",
    lingerie: "lingerie",
  },
} as const

export type CreateOptionGroup = keyof typeof CREATE_OPTION_LABEL_KEYS

function optionLabelKey(group: CreateOptionGroup, value: string | null | undefined): string | null {
  if (!value) return null
  const keys = CREATE_OPTION_LABEL_KEYS[group] as Record<string, string>
  return keys[value] ?? null
}

export function localizeCreateOptionValue(
  group: CreateOptionGroup,
  value: string | null | undefined,
  t: Translator
): string | null {
  const key = optionLabelKey(group, value)
  return key ? t(`${group}.${key}`) : value?.trim() || null
}

export function localizeCreateOptions<T extends LocalizableOption>(
  options: ReadonlyArray<T>,
  group: CreateOptionGroup,
  t: Translator
): T[] {
  return options.map((option) => {
    const label = localizeCreateOptionValue(group, option.value, t)
    if (!label) return { ...option }

    return {
      ...option,
      ...(option.name !== undefined ? { name: label } : {}),
      ...(option.label !== undefined ? { label } : {}),
    }
  })
}
